import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Loader2, Trash2 } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";

/**
 * Add / Edit Supplier modal — the UI counterpart to the BYO CSV upload.
 *
 * Used in two modes:
 *  - `mode="create"`: posts to `POST /api/suppliers`, returns the new doc
 *  - `mode="edit"`:   patches `PATCH /api/suppliers/{id}` and optionally
 *                     hard-deletes (only for manual / customer_master rows;
 *                     engine-sourced rows can't be deleted from the UI).
 *
 * Field set covers ~95% of what a risk manager fills in by hand. Anything
 * deeper (LEI, beneficial-owner chain, EU sanctions screening) flows
 * through the canonical ingestion paths.
 */

const COUNTRY_OPTIONS = [
  "GB", "US", "FR", "DE", "IT", "ES", "NL", "BE", "IE", "PT",
  "PL", "CZ", "DK", "FI", "SE", "NO", "CH", "AT",
  "CA", "MX", "BR", "AR",
  "AU", "NZ", "ZA",
  "CN", "JP", "KR", "IN", "SG", "HK", "TW", "VN", "TH", "MY", "ID", "PH",
  "AE", "SA", "IL", "TR",
  "NG", "EG", "KE",
];

const BAND_OPTIONS = [
  { value: "low", label: "Low" },
  { value: "medium", label: "Medium" },
  { value: "high", label: "High" },
  { value: "critical", label: "Critical" },
  { value: "unknown", label: "Unknown" },
];

const EMPTY = {
  legal_name: "",
  trading_name: "",
  country: "GB",
  tier: 2,
  business_unit: "",
  duns: "",
  lei: "",
  companies_house_id: "",
  industry_naics: "",
  industry_sic: "",
  distress_band: "unknown",
  materials: "",
  chokepoints: "",
};

const TESTID_BY_FIELD = {
  legal_name: "supplier-form-legal-name",
  trading_name: "supplier-form-trading-name",
  country: "supplier-form-country",
  tier: "supplier-form-tier",
  business_unit: "supplier-form-business-unit",
  duns: "supplier-form-duns",
  lei: "supplier-form-lei",
  companies_house_id: "supplier-form-companies-house-id",
  industry_naics: "supplier-form-naics",
  industry_sic: "supplier-form-sic",
  distress_band: "supplier-form-distress-band",
  materials: "supplier-form-materials",
  chokepoints: "supplier-form-chokepoints",
};

export function SupplierForm({
  open,
  onOpenChange,
  mode = "create",
  initial = null,
  onSaved,
}) {
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const isEdit = mode === "edit" && initial?.supplier_id;
  const canDelete =
    isEdit && (initial.added_via === "manual" || initial.added_via === "customer_master");

  useEffect(() => {
    if (!open) return;
    if (isEdit) {
      setForm({
        ...EMPTY,
        ...Object.fromEntries(
          Object.keys(EMPTY).map((k) => {
            const v = initial[k === "distress_band" ? "financial_distress_band" : k];
            if (Array.isArray(v)) return [k, v.join(", ")];
            return [k, v ?? EMPTY[k]];
          }),
        ),
      });
    } else {
      setForm(EMPTY);
    }
  }, [open, isEdit, initial]);

  const set = (k) => (e) =>
    setForm((p) => ({
      ...p,
      [k]: e?.target ? e.target.value : e,
    }));

  const submit = async () => {
    if (!form.legal_name.trim()) {
      toast.error("Legal name is required.");
      return;
    }
    const payload = {
      legal_name: form.legal_name.trim(),
      trading_name: form.trading_name.trim() || null,
      country: form.country || null,
      tier: parseInt(form.tier, 10) || 2,
      business_unit: form.business_unit.trim() || null,
      duns: form.duns.trim() || null,
      lei: form.lei.trim() || null,
      companies_house_id: form.companies_house_id.trim() || null,
      industry_naics: form.industry_naics.trim() || null,
      industry_sic: form.industry_sic.trim() || null,
      distress_band: form.distress_band,
      materials: splitCsv(form.materials),
      chokepoints: splitCsv(form.chokepoints),
    };
    setSaving(true);
    try {
      if (isEdit) {
        const { data } = await api.patch(`/suppliers/${initial.supplier_id}`, payload);
        toast.success("Supplier updated.");
        onSaved?.(data);
      } else {
        const { data } = await api.post("/suppliers", payload);
        toast.success(`Added "${data.legal_name}".`);
        onSaved?.(data);
      }
      onOpenChange(false);
    } catch (e) {
      toast.error(
        formatApiErrorDetail(e?.response?.data?.detail) || "Save failed",
      );
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!isEdit) return;
    if (!window.confirm(`Delete "${initial.legal_name}" and any of its supply-chain links?`))
      return;
    setDeleting(true);
    try {
      await api.delete(`/suppliers/${initial.supplier_id}`);
      toast.success("Supplier deleted.");
      onSaved?.({ deleted: initial.supplier_id });
      onOpenChange(false);
    } catch (e) {
      toast.error(
        formatApiErrorDetail(e?.response?.data?.detail) || "Delete failed",
      );
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-2xl"
        data-testid="supplier-form-dialog"
      >
        <DialogHeader>
          <DialogTitle>{isEdit ? "Edit supplier" : "Add supplier"}</DialogTitle>
          <DialogDescription>
            Fields marked * are required. Identifiers (DUNS, LEI, Companies
            House) help the screening engine deduplicate across imports and
            external feeds — leave blank if you don't have them.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Field label="Legal name *" testid={TESTID_BY_FIELD.legal_name}>
            <input
              type="text"
              required
              value={form.legal_name}
              onChange={set("legal_name")}
              data-testid={TESTID_BY_FIELD.legal_name}
              className={inputCls}
              placeholder="e.g. Acme Industrial Ltd"
            />
          </Field>
          <Field label="Trading name" testid={TESTID_BY_FIELD.trading_name}>
            <input
              type="text"
              value={form.trading_name}
              onChange={set("trading_name")}
              data-testid={TESTID_BY_FIELD.trading_name}
              className={inputCls}
            />
          </Field>

          <Field label="Country (ISO-2)" testid={TESTID_BY_FIELD.country}>
            <select
              value={form.country}
              onChange={set("country")}
              data-testid={TESTID_BY_FIELD.country}
              className={inputCls}
            >
              {COUNTRY_OPTIONS.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </Field>

          <Field label="Tier (1–5)" testid={TESTID_BY_FIELD.tier}>
            <select
              value={form.tier}
              onChange={set("tier")}
              data-testid={TESTID_BY_FIELD.tier}
              className={inputCls}
            >
              {[1, 2, 3, 4, 5].map((t) => (
                <option key={t} value={t}>
                  T{t}
                </option>
              ))}
            </select>
          </Field>

          <Field label="Business unit" testid={TESTID_BY_FIELD.business_unit}>
            <input
              type="text"
              value={form.business_unit}
              onChange={set("business_unit")}
              data-testid={TESTID_BY_FIELD.business_unit}
              className={inputCls}
              placeholder="e.g. EMEA manufacturing"
            />
          </Field>

          <Field label="Distress band" testid={TESTID_BY_FIELD.distress_band}>
            <select
              value={form.distress_band}
              onChange={set("distress_band")}
              data-testid={TESTID_BY_FIELD.distress_band}
              className={inputCls}
            >
              {BAND_OPTIONS.map((b) => (
                <option key={b.value} value={b.value}>
                  {b.label}
                </option>
              ))}
            </select>
          </Field>

          <Field label="DUNS" testid={TESTID_BY_FIELD.duns}>
            <input
              type="text"
              value={form.duns}
              onChange={set("duns")}
              data-testid={TESTID_BY_FIELD.duns}
              className={inputCls}
              placeholder="9 digits"
            />
          </Field>
          <Field label="LEI" testid={TESTID_BY_FIELD.lei}>
            <input
              type="text"
              value={form.lei}
              onChange={set("lei")}
              data-testid={TESTID_BY_FIELD.lei}
              className={inputCls}
              placeholder="20-character ISO 17442"
            />
          </Field>
          <Field
            label="Companies House CRN"
            testid={TESTID_BY_FIELD.companies_house_id}
          >
            <input
              type="text"
              value={form.companies_house_id}
              onChange={set("companies_house_id")}
              data-testid={TESTID_BY_FIELD.companies_house_id}
              className={inputCls}
              placeholder="UK 8-digit"
            />
          </Field>
          <Field label="NAICS" testid={TESTID_BY_FIELD.industry_naics}>
            <input
              type="text"
              value={form.industry_naics}
              onChange={set("industry_naics")}
              data-testid={TESTID_BY_FIELD.industry_naics}
              className={inputCls}
            />
          </Field>
          <Field label="SIC" testid={TESTID_BY_FIELD.industry_sic}>
            <input
              type="text"
              value={form.industry_sic}
              onChange={set("industry_sic")}
              data-testid={TESTID_BY_FIELD.industry_sic}
              className={inputCls}
            />
          </Field>

          <Field
            label="Materials supplied (comma-separated)"
            testid={TESTID_BY_FIELD.materials}
            className="sm:col-span-2"
          >
            <input
              type="text"
              value={form.materials}
              onChange={set("materials")}
              data-testid={TESTID_BY_FIELD.materials}
              className={inputCls}
              placeholder="e.g. steel, lithium, semiconductors"
            />
          </Field>
          <Field
            label="Chokepoints (comma-separated)"
            testid={TESTID_BY_FIELD.chokepoints}
            className="sm:col-span-2"
          >
            <input
              type="text"
              value={form.chokepoints}
              onChange={set("chokepoints")}
              data-testid={TESTID_BY_FIELD.chokepoints}
              className={inputCls}
              placeholder="e.g. single-source, Suez transit"
            />
          </Field>
        </div>

        <DialogFooter className="sm:justify-between">
          <div>
            {canDelete && (
              <button
                type="button"
                onClick={remove}
                disabled={deleting}
                className="inline-flex items-center gap-1.5 rounded-md border border-rose-300 bg-white px-3 py-2 text-sm font-medium text-rose-700 hover:bg-rose-50 disabled:opacity-50"
                data-testid="supplier-form-delete"
              >
                {deleting ? (
                  <Loader2 size={14} className="animate-spin" />
                ) : (
                  <Trash2 size={14} />
                )}
                Delete
              </button>
            )}
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              className="rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-ek-ink hover:bg-slate-50"
              data-testid="supplier-form-cancel"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={submit}
              disabled={saving}
              className="inline-flex items-center gap-1.5 rounded-md bg-ek-purple px-3 py-2 text-sm font-medium text-white hover:bg-ek-purple/90 disabled:opacity-50"
              data-testid="supplier-form-submit"
            >
              {saving && <Loader2 size={14} className="animate-spin" />}
              {isEdit ? "Save changes" : "Add supplier"}
            </button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const Field = ({ label, children, testid, className = "" }) => (
  <label
    htmlFor={testid}
    className={`flex flex-col text-xs font-medium text-ek-ink/75 ${className}`}
  >
    {label}
    <div className="mt-1">{children}</div>
  </label>
);

const inputCls =
  "w-full rounded-md border border-slate-300 bg-white px-2.5 py-1.5 text-sm text-ek-ink focus:border-ek-purple focus:outline-none focus:ring-1 focus:ring-ek-purple/30";

const splitCsv = (s) =>
  (s || "")
    .split(",")
    .map((x) => x.trim())
    .filter(Boolean);
